import { useState, useEffect, useRef } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, Check, ArrowRightLeft, ScanSearch, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../utils/apiClient";
import RecalculateReadingsButton from "./RecalculateReadingsButton";

const fmt = (v) => (v ? Number(v).toLocaleString("en-IN") : "—");

const ContextCell = ({ label, km, hr, testId }) => (
  <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{label}</p>
    <p className="font-mono text-sm">KM: <strong>{fmt(km)}</strong></p>
    <p className="font-mono text-sm">Hr: <strong>{fmt(hr)}</strong></p>
  </div>
);

const ReadingsWizardTab = ({ projectId, isMasterAdmin }) => {
  const [machines, setMachines] = useState([]);
  const [machineId, setMachineId] = useState("");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ km: "", hr: "" });
  const [fixed, setFixed] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const scanId = useRef(0);

  const scan = async () => {
    const id = ++scanId.current;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/equipment/reading-anomalies/${projectId}`);
      if (id !== scanId.current) return;
      setMachines(data);
      setFixed({});
      setStep(0);
      setMachineId(cur => cur && data.some(m => m.equipment_id === cur) ? cur
        : (data.find(m => (m.equipment_reg || "").includes("5431")) || data[0])?.equipment_id || "");
    } catch (e) {
      if (id === scanId.current) toast.error("Failed to scan readings");
    }
    if (id === scanId.current) setLoading(false);
  };

  useEffect(() => { if (projectId) scan(); }, [projectId]);

  const machine = machines.find(m => m.equipment_id === machineId);
  const entries = machine?.entries || [];
  const entry = entries[step];

  useEffect(() => {
    if (entry) setForm({ km: entry.closing_km_reading ? String(entry.closing_km_reading) : "", hr: entry.closing_hour_reading ? String(entry.closing_hour_reading) : "" });
  }, [machineId, step, entry?.id]);

  const moveHourToKm = () => setForm({ km: form.hr, hr: "" });
  const go = (d) => setStep(s => Math.min(Math.max(0, s + d), entries.length - 1));

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/equipment/fuel-issue/${entry.id}`, {
        closing_km_reading: parseFloat(form.km) || 0, closing_hour_reading: parseFloat(form.hr) || 0, allow_clear: true
      });
      const km = parseFloat(form.km) || 0, hr = parseFloat(form.hr) || 0;
      setMachines(ms => ms.map(m => m.equipment_id !== machineId ? m : {
        ...m, entries: m.entries.map(e => e.id === entry.id ? { ...e, closing_km_reading: km, closing_hour_reading: hr } : e)
      }));
      setFixed(f => ({ ...f, [entry.id]: true }));
      toast.success(`Saved ${machine.equipment_name} • ${entry.date} readings`);
      if (step < entries.length - 1) go(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save readings");
    }
    setSaving(false);
  };

  const fieldCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary";
  const fixedCount = entries.filter(e => fixed[e.id]).length;

  return (
    <div className="space-y-4" data-testid="readings-wizard-tab" style={{ maxWidth: "100%" }}>
      <div className="stat-card">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto sm:min-w-[280px]">
            <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-widest">Machine</label>
            <select value={machineId} onChange={(e) => { setMachineId(e.target.value); setStep(0); }} className={fieldCls} data-testid="wizard-machine-select">
              {machines.map(m => (
                <option key={m.equipment_id} value={m.equipment_id}>{m.equipment_name} ({m.equipment_reg}) — {m.entries.length} flagged</option>
              ))}
            </select>
          </div>
          <button onClick={scan} disabled={loading} className="btn-primary flex items-center gap-2" data-testid="wizard-rescan-btn">
            <ScanSearch className="w-4 h-4" /> {loading ? "Scanning..." : "Re-scan"}
          </button>
          {isMasterAdmin && <RecalculateReadingsButton onDone={scan} />}
        </div>
        <p className="text-xs text-slate-500 mt-3">Flags: readings going backwards, unusual jumps (e.g. KM typed into the Hour field) and missing/unchanged readings.</p>
      </div>

      {!loading && machines.length === 0 && (
        <div className="stat-card text-center py-10 text-slate-500" data-testid="wizard-all-clear">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" /> No suspicious readings found.
        </div>
      )}

      {entry && (
        <div className="stat-card space-y-4" data-testid="wizard-step-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-heading text-lg font-bold text-primary">{machine.equipment_name}</p>
              <p className="text-sm text-slate-600 font-mono">{entry.date} • {entry.issue_number} • {entry.quantity} L</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium" data-testid="wizard-step-indicator">Issue {step + 1} of {entries.length}</p>
              <p className="text-xs text-green-700">{fixedCount} saved</p>
            </div>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${((step + 1) / entries.length) * 100}%` }} />
          </div>

          <div className="space-y-1" data-testid="wizard-flags">
            {entry.flags.map((f, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {f.message}
              </div>
            ))}
            {fixed[entry.id] && <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-sm text-sm text-green-700"><Check className="w-4 h-4" /> Saved — re-scan to re-check</div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <ContextCell label="Previous closing" km={entry.opening_km_reading} hr={entry.opening_hour_reading} testId="wizard-prev" />
            <div className="p-3 border-2 border-primary rounded-sm space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-primary">This entry — closing</p>
              {machine.reading_type !== "hours" && <input type="number" step="0.1" placeholder="KM reading" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} className={fieldCls} disabled={!isMasterAdmin} data-testid="wizard-km-input" />}
              {machine.reading_type !== "km" && <input type="number" step="0.1" placeholder="Hour reading" value={form.hr} onChange={(e) => setForm({ ...form, hr: e.target.value })} className={fieldCls} disabled={!isMasterAdmin} data-testid="wizard-hr-input" />}
              {isMasterAdmin && machine.reading_type !== "hours" && entry.flags.some(f => f.type === "km_in_hour") && (
                <button onClick={moveHourToKm} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="wizard-move-hr-to-km">
                  <ArrowRightLeft className="w-3 h-3" /> Move Hour value to KM
                </button>
              )}
            </div>
            <ContextCell label={`Next entry${entry.next_date ? ` (${entry.next_date})` : ""}`} km={entry.next_km} hr={entry.next_hour} testId="wizard-next" />
          </div>

          <div className="flex flex-wrap justify-between gap-2 pt-2">
            <button onClick={() => go(-1)} disabled={step === 0} className="btn-outline flex items-center gap-1" data-testid="wizard-back-btn">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="flex gap-2">
              <button onClick={() => go(1)} disabled={step >= entries.length - 1} className="btn-outline flex items-center gap-1" data-testid="wizard-skip-btn">
                Skip <ChevronRight className="w-4 h-4" />
              </button>
              {isMasterAdmin ? (
                <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-1" data-testid="wizard-save-btn">
                  <Check className="w-4 h-4" /> {saving ? "Saving..." : "Save & Next"}
                </button>
              ) : (
                <span className="text-xs text-slate-500 self-center">Only Master Admin can correct readings</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadingsWizardTab;
